import {PIXI} from '@colonist/pixi'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {PlayerAvatarUsernameLocation, UIGamePlayerAvatarView} from './UIGamePlayerAvatarView'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {getCountryOrContinentImageLink} from '../../ui_utils/CountryFlagUtils'
import {GameFont} from '../../GameFont'
import {GameColor} from '../../GameColor'
import {SpriteButtonView} from '../../ui_canvas_engine/SpriteButtonView'
import {UIPlayerOptionsPopup} from '../../ui_player_options_popup/UIPlayerOptionsPopup'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {CountryCodes} from '@colonist/leaderboard-types'
import {UIGameManager} from '../UIGameManager'
import {navigateToPage} from '../../ui_utils/ClientUtils'
import {uiManagerEvents} from '../../EntryShared.ts'

export enum PlayerAvatarButtonTypes {
    MembershipPlus,
    MembershipPremium,
    MembershipElite,
    AddFriend,
    Holiday2023AllAccessPass
}

interface ButtonProperties {
    type: PlayerAvatarButtonTypes
    texture: PIXI.Texture
    position: PIXI.Point
    onClick: (event: any) => void
}

export class UIGamePlayerAvatarButtonView extends UIGamePlayerAvatarView {
    private readonly uiGameManager: UIGameManager
    private readonly buttonProperties: ButtonProperties[]

    constructor(uiGameManager: UIGameManager, x: number, y: number, size: number, container: PIXI.Container, playerState: PlayerValidator, showPlayerName: boolean = false, playerNameLocation: PlayerAvatarUsernameLocation = PlayerAvatarUsernameLocation.Bottom,
                playerNameFontSize: GameFont.Size = GameFont.Size.Small, playerNameColor: GameColor.Color = GameColor.Color.Gray0) {
        super(x, y, size, container, playerState, showPlayerName, playerNameLocation, playerNameFontSize, playerNameColor)
        this.uiGameManager = uiGameManager
        this.playerName?.disableTip()
        this.playerName?.makeBold()

        const buttonPositionTopLeft = new PIXI.Point(-this.bgIcon.width / 8, -this.bgIcon.width / 16)
        const buttonPositionBottomRight = new PIXI.Point(this.bgIcon.width * 0.625, this.bgIcon.width / 2)

        this.buttonProperties = [
            {type: PlayerAvatarButtonTypes.AddFriend, texture: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_add_friend), position: buttonPositionTopLeft, onClick: () => {
                this.clickOnPlayerAction()
            }},
            {type: PlayerAvatarButtonTypes.MembershipPlus, texture: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.membership_icon_plus), position: buttonPositionBottomRight, onClick: () => {
                navigateToPage('/store#membership', true)
            }},
            {type: PlayerAvatarButtonTypes.MembershipPremium, texture: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.membership_icon_premium), position: buttonPositionBottomRight, onClick: () => {
                navigateToPage('/store#membership', true)
            }},
            {type: PlayerAvatarButtonTypes.MembershipElite, texture: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.membership_icon_elite), position: buttonPositionBottomRight, onClick: () => {
                navigateToPage('/store#membership', true)
            }},
            {type: PlayerAvatarButtonTypes.Holiday2023AllAccessPass, texture: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.membership_icon_holiday_2023), position: buttonPositionBottomRight, onClick: () => {
                navigateToPage('/store#membership', true)
            }},
        ]
    }

    clickOnPlayerAction() {
        const isMyPlayer = this.playerState.color == this.uiGameManager.gameController.myColor
        if(isMyPlayer) {
            uiManagerEvents.openProfilePage(this.playerState.username)
            return
        }
        const canvas = this.uiGameManager.canvasController
        const popupPositionRelativeToScreen = this.playerIcon.getGlobalPosition()
        popupPositionRelativeToScreen.y = (popupPositionRelativeToScreen.y / canvas.appH)
        popupPositionRelativeToScreen.x = ((popupPositionRelativeToScreen.x + this.playerIcon.width * 5) / canvas.appW)
        new UIPlayerOptionsPopup(this.uiGameManager, popupPositionRelativeToScreen, this.playerState).buildEndGamePlayerOptionsPopup()
    }

    makePlayerAvatarClickable() {
        this.playerIcon.setClickAction(() => {
            this.clickOnPlayerAction()
        })
    }

    makePlayerNameClickable() {
        this.playerName?.setClickAction(() => {
            this.clickOnPlayerAction()
        })
    }

    addButton(type: PlayerAvatarButtonTypes) {
        const buttonProperties = this.getButtonProperties(type)
        if(buttonProperties == undefined) return

        const buttonSize = this.bgIcon.width / 2
        const iconSizeRatio = 1

        const button = new SpriteButtonView(buttonProperties.position.x, buttonProperties.position.y, buttonSize, buttonSize, this)
        button.viewContainer.makeRoundedBgInvisible()
        button.addCenteredIcon(buttonProperties.texture, iconSizeRatio)

        button.updateAction(buttonProperties.onClick)
    }

    private getButtonProperties(type: PlayerAvatarButtonTypes): ButtonProperties | undefined {
        return this.buttonProperties.find((buttonProperties) => buttonProperties.type == type)
    }

    drawCountryFlag(countryCode: CountryCodes | undefined) {
        const countryLink = getCountryOrContinentImageLink(countryCode)

        const flagSize = this.bgIcon.width / 2
        const countryImage = countryLink ? PIXI.Texture.from(countryLink) : PIXI.Texture.EMPTY
        const flagPosition = this.getFlagPosition(flagSize)

        new SpriteView(flagPosition.x, flagPosition.y, flagSize, flagSize, this, countryImage)
    }

    private getFlagPosition(flagSize: number): PIXI.Point {
        const gapBetweenNameAndFlagMultiplier = 0.2
        const flagPosition = new PIXI.Point()

        flagPosition.x = this.bgIcon.x + this.bgIcon.width * 1.2 + gapBetweenNameAndFlagMultiplier * this.bgIcon.width
        flagPosition.y = this.bgIcon.y + (this.bgIcon.height - flagSize) / 2

        const name = this.playerName
        if(name != undefined && this.playerNameLocation == PlayerAvatarUsernameLocation.Right) {
            flagPosition.x += name.width
        }

        return flagPosition
    }
}
